import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getContainedPlaces, getPlace, getPolygonPlaces, searchPlaces, openPlaceContinuationDraft } from './services/api';
import { mapStyle, roadLabelLayers, roadLabelSource } from './mapStyle';
import { setAppLanguage } from './i18n';
import { useThemeMode } from './AppProviders';
import PlaceDetailsSheet from './components/place/PlaceSheet';
import SearchOverlay from './components/search/SearchOverlay';
import HeaderControls from './components/layout/HeaderControls';
import AboutDialog from './components/layout/AboutDialog';
import PlaceEditDialog from './components/place/PlaceEditDialog';
import MapActions from './components/map/MapActions';
import createPlaceLabelImage from './components/map/createPlaceLabelImage';
import usePlaceLoadRealtime from './hooks/usePlaceLoadRealtime';
import {
  hasPolygon,
  footprintCoordinates,
  loadPresentation,
  placesToGeoJson,
  placeLabel,
} from './domain/places/presentation';
import { Close, SearchRounded } from '@mui/icons-material';

const mapLibreWorkerUrl = `${process.env.PUBLIC_URL || ''}/maplibre-gl-csp-worker.js`;
maplibregl.setWorkerUrl(mapLibreWorkerUrl);
if (maplibregl.getRTLTextPluginStatus?.() === 'unavailable') {
  maplibregl
    .setRTLTextPlugin(
      'https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js',
      true,
    )
    .catch(() => { });
}

const DEFAULT_CENTER = { latitude: 29.5577, longitude: 34.9519 };
const AUTO_COMPLEX_ENTER_COVERAGE = 0.38;
const AUTO_COMPLEX_EXIT_COVERAGE = 0.40;
const AUTO_COMPLEX_EXIT_DISTANCE_METERS = 250;

const pointInRing = ([x, y], ring = []) => {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const [xi, yi] = ring[index] || [];
    const [xj, yj] = ring[previous] || [];
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const intersects = ((yi > y) !== (yj > y)) &&
      (x < ((xj - xi) * (y - yi)) / ((yj - yi) || Number.EPSILON) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
};

const pointInFootprint = (point, footprint) => {
  const polygons = footprint?.type === 'MultiPolygon'
    ? footprint.coordinates
    : footprint?.type === 'Polygon' ? [footprint.coordinates] : [];
  return polygons.some((polygon) => (
    pointInRing(point, polygon?.[0]) &&
    !(polygon || []).slice(1).some((hole) => pointInRing(point, hole))
  ));
};

const footprintScreenCoverage = (footprint, latitude, zoom, viewportWidth, viewportHeight) => {
  const points = footprint?.type === 'MultiPolygon'
    ? (footprint.coordinates || []).flat(2)
    : footprint?.type === 'Polygon' ? (footprint.coordinates || []).flat(1) : [];
  const valid = points.filter((point) => Array.isArray(point) && Number.isFinite(Number(point[0])) && Number.isFinite(Number(point[1])));
  if (!valid.length || !viewportWidth || !viewportHeight || !Number.isFinite(Number(zoom))) return 0;
  const longitudes = valid.map((point) => Number(point[0]));
  const latitudes = valid.map((point) => Number(point[1]));
  const latitudeScale = Math.max(Math.cos((Number(latitude) * Math.PI) / 180), 0.2);
  const widthMeters = (Math.max(...longitudes) - Math.min(...longitudes)) * 111320 * latitudeScale;
  const heightMeters = (Math.max(...latitudes) - Math.min(...latitudes)) * 111320;
  const metersPerPixel = (156543.03392 * latitudeScale) / (2 ** Number(zoom));
  return Math.max(widthMeters / metersPerPixel / viewportWidth, heightMeters / metersPerPixel / viewportHeight);
};
const normalizeBearing = (value) => ((value % 360) + 360) % 360;
const bearingDifference = (first, second) => Math.abs(((first - second + 540) % 360) - 180);
const coordinateBearing = ([startLng, startLat], [endLng, endLat]) => {
  const lat1 = startLat * Math.PI / 180;
  const lat2 = endLat * Math.PI / 180;
  const deltaLng = (endLng - startLng) * Math.PI / 180;
  return normalizeBearing(Math.atan2(
    Math.sin(deltaLng) * Math.cos(lat2),
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng),
  ) * 180 / Math.PI);
};
const frontageBearing = (place) => {
  const frontage = place?.frontage?.type === 'LineString' ? place.frontage.coordinates : null;
  const footprint = footprintCoordinates(place?.footprint);
  if (!Array.isArray(frontage) || frontage.length !== 2 || !footprint.length) return null;
  const midpoint = [(Number(frontage[0][0]) + Number(frontage[1][0])) / 2, (Number(frontage[0][1]) + Number(frontage[1][1])) / 2];
  const center = footprint.reduce(([lng, lat], point) => [lng + Number(point[0]) / footprint.length, lat + Number(point[1]) / footprint.length], [0, 0]);
  const lineBearing = coordinateBearing(frontage[0].map(Number), frontage[1].map(Number));
  const inwardBearing = coordinateBearing(midpoint, center);
  const firstNormal = normalizeBearing(lineBearing + 90);
  const secondNormal = normalizeBearing(lineBearing - 90);
  return bearingDifference(firstNormal, inwardBearing) <= bearingDifference(secondNormal, inwardBearing) ? firstNormal : secondNormal;
};
const sx = {
  app: {
    position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
    bgcolor: 'background.default',
  },
  map: { position: 'absolute', inset: 0 },
  sheet: {
    position: 'absolute', zIndex: 8, right: '50%', bottom: 0,
    width: { xs: '100%', sm: 'min(760px, calc(100vw - 32px))' },
    minHeight: 300, p: { xs: '30px 18px 18px', sm: '31px 32px 23px' },
    border: '1px solid', borderColor: 'divider', borderBottom: 0,
    borderRadius: { xs: '24px 24px 0 0', sm: '28px 28px 0 0' },
    bgcolor: 'background.paper', boxShadow: '0 -20px 65px rgba(17,39,35,.2)',
    backdropFilter: 'blur(20px)', transform: 'translateX(50%)',
    animation: 'sheet-in .32s cubic-bezier(.22,.8,.3,1)',
  },
  sheetHandle: {
    position: 'absolute', top: 11, right: '50%', width: 52, height: 5,
    borderRadius: 99, bgcolor: 'text.disabled', transform: 'translateX(50%)',
  },
  sheetClose: {
    position: 'absolute', top: 19, left: 21, width: 39, height: 39,
    bgcolor: 'action.hover', color: 'text.secondary',
  },
  sheetLoading: {
    minHeight: 230, color: 'text.secondary', fontWeight: 800,
    alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 1.5,
  },
  placeHeading: { justifyContent: 'space-between', alignItems: 'center', gap: 2.5 },
  eyebrow: { m: '0 0 7px', color: 'primary.main', fontSize: 12, fontWeight: 900 },
  placeTitle: {
    m: 0, pr: 0, pl: 6, fontSize: { xs: 27, sm: 'clamp(25px, 4vw, 38px)' },
    lineHeight: 1.1, fontWeight: 500,
  },
  address: { mt: 1, mb: 0, color: 'text.secondary' },
  contained: {
    mt: 2.4, px: 2, py: 1.5, borderRadius: '13px',
    color: 'info.dark', bgcolor: 'action.hover',
  },
  facts: {
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
    gap: 1.25, mt: 2.6,
    '& > .MuiPaper-root': {
      display: 'grid', gap: 1, minHeight: 84, p: 1.75,
      borderRadius: '16px', bgcolor: 'action.hover',
    },
    '& span': { color: 'text.secondary', fontSize: 12 },
    '& strong': { fontSize: 17 },
  },
  viewOnly: { mt: 2.1, mb: 0, color: 'text.secondary', textAlign: 'center', fontSize: 12 },
  brand: {
    position: 'absolute', top: { xs: 12, sm: 22 }, right: { xs: 12, sm: 24 },
    zIndex: 4, alignItems: 'center', gap: 1.1, minHeight: { xs: 42, sm: 48 },
    px: { xs: 1.5, sm: 2.1 }, border: '1px solid', borderColor: 'divider',
    borderRadius: '17px', bgcolor: 'background.paper',
    boxShadow: '0 10px 35px rgba(17,39,35,.12)', backdropFilter: 'blur(18px)',
    fontSize: { xs: 15, sm: 18 },
  },
  brandDot: {
    width: 13, height: 13, border: '4px solid #ddebff',
    borderRadius: '50%', bgcolor: 'primary.main',
  },
  displayControls: {
    position: 'absolute', zIndex: 6, top: { xs: 12, sm: 22 }, left: { xs: 12, sm: 24 },
    alignItems: 'center', gap: 1, minHeight: { xs: 42, sm: 48 },
    py: { xs: 0.25, sm: 0.6 }, pr: 0.9, pl: 1.5,
    border: '1px solid', borderColor: 'divider', borderRadius: '17px',
    bgcolor: 'background.paper', boxShadow: '0 10px 35px rgba(17,39,35,.12)',
    backdropFilter: 'blur(18px)',
    '& .MuiSelect-select': { minWidth: { xs: 62, sm: 72 }, py: '5px !important', fontWeight: 750 },
  },
  displayIcon: { color: 'text.secondary', display: { xs: 'none', sm: 'block' } },
  divider: { width: 1, height: 26, bgcolor: 'divider' },
  searchShell: {
    position: 'absolute', zIndex: 5, top: { xs: 65, sm: 22 }, left: '50%',
    width: { xs: 'calc(100vw - 24px)', sm: 'min(620px, calc(100vw - 260px))' },
    transform: 'translateX(-50%)',
  },
  searchBar: {
    display: 'flex', alignItems: 'center', gap: 1, minHeight: { xs: 54, sm: 58 },
    p: '7px 8px 7px 16px', border: '1px solid', borderColor: 'divider',
    borderRadius: { xs: '17px', sm: '20px' }, bgcolor: 'background.paper',
    boxShadow: '0 18px 60px rgba(17,39,35,.18)', backdropFilter: 'blur(18px)',
  },
  searchIcon: { color: 'text.secondary', fontSize: 28, transform: 'rotate(-20deg)' },
  searchInput: {
    flex: 1, minWidth: 0,
    '& .MuiInputBase-root': { fontSize: 17, fontWeight: 650 },
    '& input': { color: 'text.primary', textAlign: 'start' },
  },
  clearButton: { width: 36, minHeight: 36, color: 'text.secondary', bgcolor: 'action.hover' },
  searchButton: {
    minHeight: 44, px: { xs: 1.75, sm: 2.5 }, borderRadius: '14px',
    color: 'white', background: 'linear-gradient(135deg, #2888ff, #1269e8)', fontWeight: 800,
  },
  results: {
    maxHeight: { xs: '48vh', sm: 'min(58vh, 520px)' }, mt: 1.25, p: 1.5,
    overflow: 'auto', border: '1px solid', borderColor: 'divider',
    borderRadius: '20px', bgcolor: 'background.paper',
    boxShadow: '0 18px 60px rgba(17,39,35,.18)', backdropFilter: 'blur(18px)',
  },
  resultsHeading: {
    display: { xs: 'grid', sm: 'flex' }, flexDirection: 'row',
    justifyContent: 'space-between', gap: { xs: 0.5, sm: 1.5 }, p: '4px 7px 12px',
    '& span': { color: 'text.secondary', fontSize: 12 },
  },
  resultRow: {
    width: '100%', minHeight: 68, display: 'flex', alignItems: 'center',
    gap: 1.4, p: '9px 10px', borderRadius: '14px', color: 'text.primary',
    justifyContent: 'flex-start', textAlign: 'start',
    '&:hover': { bgcolor: 'action.hover' },
  },
  rank: {
    display: 'grid', placeItems: 'center', width: 28, height: 28,
    flex: '0 0 auto', borderRadius: '50%', bgcolor: 'action.hover',
    fontSize: 12, fontWeight: 900,
  },
  loadDot: {
    width: 12, height: 12, flex: '0 0 auto', border: '3px solid white',
    borderRadius: '50%', boxShadow: '0 0 0 1px rgba(0,0,0,.08)',
  },
  resultCopy: {
    minWidth: 0, flex: 1, gap: 0.5,
    '& strong, & small': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    '& small': { color: 'text.secondary' },
  },
  resultArrow: { color: 'text.secondary', fontSize: 29 },
  toast: {
    position: 'absolute', zIndex: 7, right: '50%', bottom: 24,
    px: 2, py: 1.25, borderRadius: '12px', transform: 'translateX(50%)',
    boxShadow: '0 8px 25px rgba(17,39,35,.15)', fontSize: 13, fontWeight: 700,
  },
};

export default function App() {
  const { t, i18n } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const language = i18n.resolvedLanguage || i18n.language || 'en';
  const isMobileLayout = useMediaQuery(
    '(max-width: 599.95px), (max-height: 499.95px)',
    { noSsr: true },
  );
  const isContinuationMobile = useMediaQuery('(max-width: 899.95px)', { noSsr: true });
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const userMarkerRef = useRef(null);
  const polygonTooltipRef = useRef(null);
  const placesRef = useRef([]);
  const placeTransitionRef = useRef({ frame: null, generation: 0, animating: false });
  const mapRevealProgressRef = useRef(0);
  const placeFocusGenerationRef = useRef(0);
  const viewportBeforePlaceRef = useRef(null);
  const viewportBeforeComplexRef = useRef(null);
  const autoComplexEntryRef = useRef(false);
  const blockedAutoComplexIdRef = useRef(null);
  const blockedZoomComplexIdRef = useRef(null);
  const userPositionRef = useRef(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [userPosition, setUserPosition] = useState(null);
  userPositionRef.current = userPosition;
  const [polygonPlaces, setPolygonPlaces] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [floorPlaces, setFloorPlaces] = useState([]);
  const [complexMode, setComplexMode] = useState(null);
  const [complexQuery, setComplexQuery] = useState('');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState('loading');
  const [error, setError] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(18);
  const [renderedMapPlaces, setRenderedMapPlaces] = useState([]);
  const [mapRevealProgress, setMapRevealProgress] = useState(0);
  mapRevealProgressRef.current = mapRevealProgress;
  const [newPlaceDraft, setNewPlaceDraft] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [continuationDesktopOnlyOpen, setContinuationDesktopOnlyOpen] = useState(false);

  const complexSearchResults = useMemo(() => {
    const needle = complexQuery.trim().toLocaleLowerCase(language);
    if (!needle || !complexMode) return [];
    const values = (value) => (Array.isArray(value) ? value : [value])
      .filter(Boolean)
      .map((item) => typeof item === 'string'
        ? item
        : item.displayName || item.name || item.systemName || '')
      .filter(Boolean);
    return (complexMode.floors || [])
      .flatMap((floor) => (floor.places || []).map((place) => ({ place, floor })))
      .map((result) => {
        const names = values([
          result.place.name, result.place.shortName,
          result.place.displayName, result.place.branchName,
        ]).map((value) => value.toLocaleLowerCase(language));
        const categories = values(result.place.categories)
          .map((value) => value.replaceAll('_', ' ').toLocaleLowerCase(language));
        const score = names.some((value) => value === needle) ? 0
          : names.some((value) => value.startsWith(needle)) ? 1
            : names.some((value) => value.includes(needle)) ? 2
              : categories.some((value) => value.includes(needle)) ? 3 : 99;
        return { ...result, score };
      })
      .filter(({ score }) => score < 99)
      .sort((a, b) => a.score - b.score || placeLabel(a.place).localeCompare(placeLabel(b.place), language))
      .slice(0, 6);
  }, [complexMode, complexQuery, language]);



  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('continuePlaceDraft');
    if (!token) return;
    if (isContinuationMobile) {
      setContinuationDesktopOnlyOpen(true);
      return;
    }
    openPlaceContinuationDraft(token)
      .then(async ({ payload }) => {
        const proposed = payload?.proposed || {};
        const coordinates = proposed.location?.coordinates || [];
        if (payload?.kind === 'place-edit-geometry' && payload?.placeId) {
          const snapshot = payload?.snapshot;
          const snapshotPlace = snapshot?.place;
          const restoredPlace = snapshotPlace || await getPlace(payload.placeId);
          const restoredLocation = snapshot?.businessLocation;
          setNewPlaceDraft({
            ...restoredPlace,
            id: payload.placeId,
            isContinuationDraft: true,
            dialogMode: 'edit',
            latitude: Array.isArray(restoredLocation) ? Number(restoredLocation[1]) : restoredPlace.latitude,
            longitude: Array.isArray(restoredLocation) ? Number(restoredLocation[0]) : restoredPlace.longitude,
            reporter: snapshotPlace?.reporter || payload?.reporter || {},
            continuationForm: snapshot?.form || null,
            continuationPoints: snapshot?.points || null,
          });
        } else {
          const snapshot = payload?.snapshot;
          const restoredLocation = snapshot?.businessLocation;
          setNewPlaceDraft(snapshot?.place ? {
            ...snapshot.place,
            id: 'continued-place',
            isContinuationDraft: true,
            latitude: Array.isArray(restoredLocation) ? Number(restoredLocation[1]) : snapshot.place.latitude,
            longitude: Array.isArray(restoredLocation) ? Number(restoredLocation[0]) : snapshot.place.longitude,
            continuationForm: snapshot.form || null,
            continuationPoints: snapshot.points || null,
          } : {
            id: 'continued-place', isContinuationDraft: true,
            name: proposed.name || '', address: proposed.address || '', city: proposed.city || '', country: proposed.country || '',
            categories: Array.isArray(proposed.categories) ? proposed.categories : [],
            latitude: Number(coordinates[1]), longitude: Number(coordinates[0]),
            openingHours: proposed.openingHours || [], footprint: proposed.footprint || null,
            reporter: payload?.reporter || {},
          });
        }
        window.history.replaceState({}, '', window.location.pathname);
      })
      .catch(() => { });
  }, [isContinuationMobile]);

  const visiblePlaces = useMemo(() => {
    const focusedPlaces = floorPlaces.length ? floorPlaces : searchResults;
    if (!focusedPlaces.length) {
      return polygonPlaces.filter((place) => place.placeKind !== 'unit');
    }
    const resultIds = new Set(focusedPlaces.map((place) => place.id));
    const parentIds = new Set(focusedPlaces.map((place) => place.containment?.parentPlaceId).filter(Boolean));
    const relatedPolygons = polygonPlaces.filter((place) => (
      resultIds.has(place.id) || (!floorPlaces.length && parentIds.has(place.id))
    ));
    const byId = new Map();
    [...relatedPolygons, ...focusedPlaces].forEach((place) => byId.set(place.id, place));
    const activeContainerId = floorPlaces.length ? complexMode?.container?.id : null;
    return [...byId.values()].filter((place) => !activeContainerId || String(place.id) !== String(activeContainerId));
  }, [complexMode?.container?.id, floorPlaces, polygonPlaces, searchResults]);

  const desiredMapPlaces = useMemo(() => {
    const byId = new Map(visiblePlaces.map((place) => [String(place.id), place]));
    if (selectedPlace?.id) byId.set(String(selectedPlace.id), selectedPlace);
    return [...byId.values()];
  }, [selectedPlace, visiblePlaces]);
  const desiredMapPlacesKey = useMemo(
    () => desiredMapPlaces.map((place) => String(place.id)).sort().join('|'),
    [desiredMapPlaces],
  );
  const renderedMapPlacesKey = useMemo(
    () => renderedMapPlaces.map((place) => String(place.id)).sort().join('|'),
    [renderedMapPlaces],
  );

  useEffect(() => {
    if (!mapReady) return undefined;
    const transition = placeTransitionRef.current;
    const generation = transition.generation + 1;
    transition.generation = generation;
    if (transition.frame) cancelAnimationFrame(transition.frame);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const run = (from, to, duration, onComplete) => {
      if (reduceMotion) {
        setMapRevealProgress(to);
        onComplete?.();
        return;
      }
      const startedAt = performance.now();
      transition.animating = true;
      const tick = (now) => {
        if (transition.generation !== generation) return;
        const elapsed = Math.min(1, (now - startedAt) / duration);
        const eased = elapsed < 0.5
          ? 4 * elapsed * elapsed * elapsed
          : 1 - ((-2 * elapsed + 2) ** 3) / 2;
        setMapRevealProgress(from + ((to - from) * eased));
        if (elapsed < 1) transition.frame = requestAnimationFrame(tick);
        else {
          transition.frame = null;
          onComplete?.();
        }
      };
      transition.frame = requestAnimationFrame(tick);
    };

    const revealNext = () => {
      setRenderedMapPlaces(desiredMapPlaces);
      setMapRevealProgress(0);
      requestAnimationFrame(() => run(0, 1, 440, () => {
        transition.animating = false;
      }));
    };

    if (!renderedMapPlaces.length) revealNext();
    else run(mapRevealProgress, 0, 260, revealNext);

    return () => {
      if (transition.frame) cancelAnimationFrame(transition.frame);
    };
    // Only changing the visible place ID set starts a transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredMapPlacesKey, mapReady]);

  useEffect(() => {
    if (desiredMapPlacesKey === renderedMapPlacesKey &&
      !placeTransitionRef.current.animating && renderedMapPlaces !== desiredMapPlaces) {
      setRenderedMapPlaces(desiredMapPlaces);
    }
  }, [desiredMapPlaces, desiredMapPlacesKey, renderedMapPlaces, renderedMapPlacesKey]);
  const refreshPolygonPlaces = useCallback(() => {
    getPolygonPlaces(center)
      .then(setPolygonPlaces)
      .catch(() => setError(t('location.loadFailed')));
  }, [center, t]);

  const applyRealtimeUpdate = useCallback(({ placeId, load, openingHours }) => {
    const updatePlace = (place) => String(place.id) === String(placeId)
      ? { ...place, load, ...(openingHours ? { openingHours } : {}) }
      : place;
    setPolygonPlaces((places) => places.map(updatePlace));
    setSearchResults((places) => places.map(updatePlace));
    setFloorPlaces((places) => places.map(updatePlace));
    setSelectedPlace((place) => place ? updatePlace(place) : place);
  }, []);

  const realtimePlaceIds = useMemo(
    () => [...new Set([
      ...visiblePlaces.map((place) => place.id),
      selectedPlace?.id,
    ].filter(Boolean))],
    [selectedPlace?.id, visiblePlaces],
  );

  usePlaceLoadRealtime({
    placeIds: realtimePlaceIds,
    onUpdate: applyRealtimeUpdate,
    onRefresh: refreshPolygonPlaces,
  });

  useEffect(() => {
    placesRef.current = renderedMapPlaces;
  }, [renderedMapPlaces]);

  const openPlace = useCallback(async (place) => {
    const map = mapRef.current;
    const focusGeneration = placeFocusGenerationRef.current + 1;
    placeFocusGenerationRef.current = focusGeneration;
    if (map) {
      const mapCenter = map.getCenter();
      viewportBeforePlaceRef.current = {
        center: [mapCenter.lng, mapCenter.lat],
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      };
    }

    setPlaceLoading(true);
    setSelectedPlace(place);

    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (placeFocusGenerationRef.current !== focusGeneration || !mapRef.current) return;
      const activeMap = mapRef.current;
      const sheetElement = document.querySelector('[data-place-sheet="true"]');
      const measuredSheetHeight = Math.ceil(sheetElement?.getBoundingClientRect().height || 0);
      const maxSheetHeight = window.innerHeight * (isMobileLayout ? 0.72 : 0.82);
      const fallbackSheetHeight = isMobileLayout ? 370 : 360;
      const sheetHeight = Math.min(
        maxSheetHeight,
        Math.max(fallbackSheetHeight, measuredSheetHeight),
      );
      const targetBearing = frontageBearing(place);

      if (hasPolygon(place)) {
        const bounds = new maplibregl.LngLatBounds();
        footprintCoordinates(place.footprint).forEach((coordinate) => bounds.extend(coordinate));
        if (place?.frontage?.type === 'LineString') {
          (place.frontage.coordinates || []).forEach((coordinate) => bounds.extend(coordinate));
        }
        const padding = isMobileLayout
          ? { top: 36, right: 24, bottom: sheetHeight + 16, left: 24 }
          : { top: 42, right: 70, bottom: sheetHeight + 20, left: 70 };
        activeMap.fitBounds(bounds, {
          padding,
          maxZoom: 20,
          pitch: 40,
          ...(targetBearing !== null ? { bearing: targetBearing } : {}),
          duration: 850,
          retainPadding: false,
        });
      } else {
        activeMap.flyTo({
          center: [place.longitude, place.latitude],
          zoom: 20,
          pitch: isMobileLayout ? 58 : 80,
          ...(targetBearing !== null ? { bearing: targetBearing } : {}),
          duration: 850,
        });
      }
    }));

    try {
      const fullPlace = await getPlace(place.id);
      const resolvedPlace = fullPlace || place;
      const mergeResolvedPlace = (items) => items.map((item) => (
        String(item.id) === String(resolvedPlace.id)
          ? { ...item, ...resolvedPlace }
          : item
      ));
      setPolygonPlaces(mergeResolvedPlace);
      setSearchResults(mergeResolvedPlace);
      setFloorPlaces(mergeResolvedPlace);
      setSelectedPlace(resolvedPlace);
    } catch {
      setSelectedPlace(place);
    } finally {
      setPlaceLoading(false);
    }
  }, [isMobileLayout]);
  const closePlace = () => {
    placeFocusGenerationRef.current += 1;
    setSelectedPlace(null);
    if (complexMode) {
      const previousBearing = viewportBeforePlaceRef.current?.bearing;
      viewportBeforePlaceRef.current = null;
      if (Number.isFinite(previousBearing) && mapRef.current) {
        mapRef.current.easeTo({ bearing: previousBearing, duration: 700 });
      }
      return;
    }
    const viewport = viewportBeforePlaceRef.current;
    viewportBeforePlaceRef.current = null;
    if (viewport && mapRef.current) {
      mapRef.current.flyTo({ ...viewport, duration: 700 });
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: [DEFAULT_CENTER.longitude, DEFAULT_CENTER.latitude],
      zoom: 16,
      pitch: isMobileLayout ? 35 : 40,
      minZoom: 14.5,
      attributionControl: false,
    });
    mapRef.current = map;
    map.on('error', (event) => {
      const mapError = event?.error || event;
      console.error('MapLibre map error', {
        message: mapError?.message || String(mapError),
        stack: mapError?.stack,
        sourceId: event?.sourceId,
        tileId: event?.tile?.tileID?.canonical,
      });
    });
    map.scrollZoom.setWheelZoomRate(1 / 380);
    map.scrollZoom.setZoomRate(1 / 85);
    if (!isMobileLayout) {
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-left');
    }
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      'bottom-right',
    );
    map.on('load', () => {
      polygonTooltipRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: 'polygon-hover-tooltip',
      });
      map.addImage('place-label-background', createPlaceLabelImage(), {
        stretchX: [[28, 68]],
        stretchY: [[18, 28]],
        content: [18, 10, 78, 36],
      });
      map.addSource('place-footprints', {
        type: 'geojson',
        data: placesToGeoJson([], () => ''),
      });
      map.addLayer({
        id: 'place-footprints-shadow',
        type: 'line',
        source: 'place-footprints',
        paint: {
          'line-color': '#172033',
          'line-opacity': ['*', 0.16, ['get', 'revealProgress']],
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 17, 7],
          'line-blur': 6,
          'line-translate': [0, 1.5],
        },
      });
      map.addLayer({
        id: 'place-footprints-fill',
        type: 'fill',
        source: 'place-footprints',
        paint: {
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': ['get', 'revealProgress'],
          'fill-antialias': true,
        },
      });
      map.addLayer({
        id: 'place-footprints-line',
        type: 'line',
        source: 'place-footprints',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 0.5,
          'line-opacity': ['*', 0.82, ['get', 'revealProgress']],
        },
      });
      map.addLayer({
        id: 'place-footprints-extrusion',
        type: 'fill-extrusion',
        source: 'place-footprints',
        paint: {
          'fill-extrusion-color': ['get', 'fillColor'],
          'fill-extrusion-base': 0,
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            13.8, ['*', ['get', 'heightMultiplier'], ['get', 'revealProgress'], 1.5],
            16, ['*', ['get', 'heightMultiplier'], ['get', 'revealProgress'], 4],
            18, ['*', ['get', 'heightMultiplier'], ['get', 'revealProgress'], 8],
            20, ['*', ['get', 'heightMultiplier'], ['get', 'revealProgress'], 11],
          ],
          'fill-extrusion-opacity': 0.88,
          'fill-extrusion-vertical-gradient': true,
        },
      });
      map.addSource('roadLabels', roadLabelSource);
      roadLabelLayers.forEach((layer) => map.addLayer(layer));
      map.addLayer({
        id: 'place-footprints-label',
        type: 'symbol',
        source: 'place-footprints',
        minzoom: 17,
        layout: {
          'icon-image': 'place-label-background',
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [7, 14, 7, 14],
          'icon-allow-overlap': false,
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 13,
          'text-anchor': 'center',
          'text-padding': 8,
        },
        paint: {
          'text-color': '#172322',
          'icon-opacity': ['get', 'revealProgress'],
          'text-opacity': ['get', 'revealProgress'],
          'text-halo-color': 'rgba(255,255,255,.35)',
          'text-halo-width': 0.5,
        },
      }); map.on('click', 'place-footprints-extrusion', (event) => {
        const id = event.features?.[0]?.properties?.placeId;
        const place = placesRef.current.find((item) => item.id === id);
        if (place) openPlace(place);
      });
      map.on('mouseenter', 'place-footprints-extrusion', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mousemove', 'place-footprints-extrusion', (event) => {
        const name = event.features?.[0]?.properties?.name;
        if (!name || !polygonTooltipRef.current) return;
        const labelIsVisible = map.getZoom() >= 17;
        if (labelIsVisible) {
          polygonTooltipRef.current.remove();
          return;
        }
        polygonTooltipRef.current
          .setLngLat(event.lngLat)
          .setText(name)
          .addTo(map);
      });
      map.on('mouseleave', 'place-footprints-extrusion', () => {
        map.getCanvas().style.cursor = '';
        polygonTooltipRef.current?.remove();
      });
      setMapReady(true);
    });
    map.on('zoom', () => setMapZoom(map.getZoom()));
    return () => {
      markerRefs.current.forEach((marker) => marker.remove());
      userMarkerRef.current?.remove();
      polygonTooltipRef.current?.remove();
      polygonTooltipRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [isMobileLayout, openPlace]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('fallback');
      return;
    }
    const handlePosition = ({ coords }, centerMap = false) => {
      const nextPosition = { latitude: coords.latitude, longitude: coords.longitude };
      setUserPosition(nextPosition);
      setLocationStatus('ready');
      if (centerMap) {
        setCenter(nextPosition);
        mapRef.current?.flyTo({
          center: [nextPosition.longitude, nextPosition.latitude],
          zoom: 18,
          duration: 900,
        });
      }
    };
    navigator.geolocation.getCurrentPosition(
      (position) => handlePosition(position, true),
      () => setLocationStatus('fallback'),
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 60_000 },
    );
    const watchId = navigator.geolocation.watchPosition(
      (position) => handlePosition(position),
      () => { },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [isContinuationMobile]);

  useEffect(() => {
    if (!mapReady || locationStatus !== 'ready' || !userPosition || !mapRef.current) return;
    userMarkerRef.current?.remove();
    const userElement = document.createElement('div');
    userElement.className = 'user-location-marker';
    userElement.innerHTML = '<span></span>';
    userMarkerRef.current = new maplibregl.Marker({ element: userElement })
      .setLngLat([userPosition.longitude, userPosition.latitude])
      .addTo(mapRef.current);
  }, [locationStatus, mapReady, userPosition]);

  useEffect(() => {
    refreshPolygonPlaces();
  }, [language, refreshPolygonPlaces]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const source = mapRef.current.getSource('place-footprints');
    source?.setData(placesToGeoJson(renderedMapPlaces, t, mapRevealProgress));
  }, [mapReady, mapRevealProgress, renderedMapPlaces, t]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    markerRefs.current.forEach((marker) => marker.remove());
    markerRefs.current = [];
    renderedMapPlaces.filter((place) => !hasPolygon(place)).forEach((place) => {
      if (!Number.isFinite(Number(place.latitude)) || !Number.isFinite(Number(place.longitude))) return;
      const load = loadPresentation(place, t);
      const element = document.createElement('button');
      element.type = 'button';
      element.className = 'place-marker';
      element.dataset.placeId = String(place.id);
      element.style.zIndex = '5';
      element.style.setProperty('--marker-color', load.color);
      element.innerHTML = `<span class="place-marker-visual"><span class="marker-pin"></span><span class="marker-name">${placeLabel(place)}</span></span>`;
      const markerLabel = element.querySelector('.marker-name');
      if (markerLabel) {
        const isSelected = selectedPlace?.id === place.id;
        markerLabel.style.display = mapRef.current.getZoom() >= 17 || isSelected ? 'block' : 'none';
      }
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        openPlace(place);
      });
      element.addEventListener('mouseenter', () => {
        if (mapRef.current.getZoom() >= 17 || !polygonTooltipRef.current) return;
        polygonTooltipRef.current
          .setLngLat([Number(place.longitude), Number(place.latitude)])
          .setText(placeLabel(place))
          .addTo(mapRef.current);
      });
      element.addEventListener('mouseleave', () => {
        polygonTooltipRef.current?.remove();
      });
      markerRefs.current.push(
        new maplibregl.Marker({ element, anchor: 'bottom' })
          .setLngLat([place.longitude, place.latitude])
          .addTo(mapRef.current),
      );
    });

  }, [mapReady, openPlace, renderedMapPlaces, selectedPlace, t]);

  useEffect(() => {
    markerRefs.current.forEach((marker) => {
      const visual = marker.getElement().querySelector('.place-marker-visual');
      if (!visual) return;
      visual.style.opacity = String(mapRevealProgress);
      visual.style.transform = `translateY(${(1 - mapRevealProgress) * 28}px) scale(${0.82 + (mapRevealProgress * 0.18)})`;
    });
  }, [mapRevealProgress]);
  useEffect(() => {
    markerRefs.current.forEach((marker) => {
      const label = marker.getElement().querySelector('.marker-name');
      const placeId = marker.getElement().dataset.placeId;
      if (label) {
        label.style.display = mapZoom >= 17 ||
          String(selectedPlace?.id || '') === placeId ? 'block' : 'none';
      }
    });
  }, [mapZoom, selectedPlace]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const textField = [
      'coalesce',
      ['get', `name:${language}`],
      ['get', 'name'],
      ['get', 'ref'],
    ];
    ['major-road-labels', 'secondary-street-labels'].forEach((layerId) => {
      if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'text-field', textField);
    });
  }, [language, mapReady]);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    if (map.getLayer('carto')) {
      map.setPaintProperty('carto', 'raster-brightness-max', mode === 'dark' ? 0.62 : 1);
      map.setPaintProperty('carto', 'raster-saturation', mode === 'dark' ? -0.48 : -0.05);
      map.setPaintProperty('carto', 'raster-contrast', mode === 'dark' ? 0.2 : 0);
    }
    if (map.getLayer('place-footprints-label')) {
      map.setPaintProperty(
        'place-footprints-label',
        'text-color',
        mode === 'dark' ? '#eef6f3' : '#172322',
      );
      map.setPaintProperty(
        'place-footprints-label',
        'text-halo-color',
        mode === 'dark' ? 'rgba(17,25,23,.92)' : 'rgba(255,255,255,.96)',
      );
    }
    ['major-road-labels', 'secondary-street-labels'].forEach((layerId) => {
      if (!map.getLayer(layerId)) return;
      map.setPaintProperty(layerId, 'text-color', mode === 'dark' ? '#dce7e4' : '#455064');
      map.setPaintProperty(
        layerId,
        'text-halo-color',
        mode === 'dark' ? 'rgba(13,20,18,.92)' : 'rgba(255,255,255,.94)',
      );
    });
  }, [mapReady, mode]);

  const submitSearch = async (event) => {
    event.preventDefault();
    const cleanQuery = query.trim();
    if (!cleanQuery) return;
    setSearching(true);
    setError('');
    try {
      const liveMapCenter = mapRef.current?.getCenter();
      const searchOrigin = userPosition || (
        liveMapCenter
          ? { latitude: liveMapCenter.lat, longitude: liveMapCenter.lng }
          : center
      );
      const results = await searchPlaces({ query: cleanQuery, ...searchOrigin });
      setSearchResults(results || []);
      setFloorPlaces([]);
      if (results?.length === 1) {
        const place = results[0];
        openPlace(place);
      } else if (results?.length > 1) {
        const bounds = new maplibregl.LngLatBounds();
        results.forEach((place) => bounds.extend([place.longitude, place.latitude]));
        mapRef.current?.fitBounds(bounds, { padding: 110, maxZoom: 16.5, duration: 850 });
      }
    } catch {
      setError(t('search.failed'));
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setFloorPlaces([]);
    setSelectedPlace(null);
    mapRef.current?.flyTo({
      center: [center.longitude, center.latitude],
      zoom: 16,
      duration: 700,
    });
  };

  const selectResult = (place) => {
    openPlace(place);
  };

  const showFloorPlaces = useCallback((selection) => {
    const nextFloorPlaces = Array.isArray(selection?.places) ? selection.places : [];
    const preserveViewport = Boolean(selection?.preserveViewport);
    if (!complexMode && !viewportBeforeComplexRef.current) {
      const map = mapRef.current;
      const mapCenter = map?.getCenter();
      viewportBeforeComplexRef.current = viewportBeforePlaceRef.current || (map && mapCenter ? {
        center: [mapCenter.lng, mapCenter.lat],
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      } : null);
    }
    viewportBeforePlaceRef.current = null;
    setSelectedPlace(null);
    setSearchResults([]);
    setQuery('');
    setFloorPlaces(nextFloorPlaces);
    setComplexMode({ ...selection, entrySource: selection?.entrySource || 'manual' });
    const footprintPoints = nextFloorPlaces.flatMap((place) => footprintCoordinates(place.footprint));
    if (!preserveViewport && footprintPoints.length && mapRef.current) {
      const bounds = footprintPoints.reduce(
        (current, coordinate) => current.extend(coordinate),
        new maplibregl.LngLatBounds(),
      );
      mapRef.current.fitBounds(bounds, {
        padding: isMobileLayout
          ? { top: 150, right: 24, bottom: 120, left: 24 }
          : { top: 110, right: 90, bottom: 90, left: 90 },
        maxZoom: 20.5,
        pitch: 28,
        duration: 700,
      });
    }
  }, [complexMode, isMobileLayout]);

  const exitComplexMode = useCallback(() => {
    blockedAutoComplexIdRef.current = complexMode?.container?.id || null;
    blockedZoomComplexIdRef.current = complexMode?.container?.id || null;
    setComplexQuery('');
    setFloorPlaces([]);
    setComplexMode(null);
    const viewport = viewportBeforeComplexRef.current;
    viewportBeforeComplexRef.current = null;
    if (viewport && mapRef.current) mapRef.current.flyTo({ ...viewport, duration: 700 });
  }, [complexMode?.container?.id]);

  useEffect(() => {
    if (!userPosition || !polygonPlaces.length) return;
    const point = [Number(userPosition.longitude), Number(userPosition.latitude)];
    const container = polygonPlaces.find((place) => (
      place.placeKind === 'container' && pointInFootprint(point, place.footprint)
    ));

    if (!container) {
      blockedAutoComplexIdRef.current = null;
      if (complexMode?.entrySource === 'gps') {
        setComplexQuery('');
        setFloorPlaces([]);
        setComplexMode(null);
        viewportBeforeComplexRef.current = null;
      }
      return;
    }

    if (complexMode) {
      const activeId = String(complexMode.container?.id || '');
      if (complexMode.entrySource === 'gps' && activeId !== String(container.id)) {
        setComplexQuery('');
        setFloorPlaces([]);
        setComplexMode(null);
        viewportBeforeComplexRef.current = null;
      }
      return;
    }
    if (selectedPlace || autoComplexEntryRef.current ||
      String(blockedAutoComplexIdRef.current || '') === String(container.id)) return;
    autoComplexEntryRef.current = true;
    getContainedPlaces(container.id)
      .then((children) => {
        const latestPosition = userPositionRef.current;
        const latestPoint = latestPosition
          ? [Number(latestPosition.longitude), Number(latestPosition.latitude)]
          : null;
        const isStillInside = latestPoint && pointInFootprint(latestPoint, container.footprint);
        const isBlocked = String(blockedAutoComplexIdRef.current || '') === String(container.id);
        if (!isStillInside || isBlocked) return;
        const grouped = new Map();
        (Array.isArray(children) ? children : []).forEach((child) => {
          const floor = String(child?.containment?.floor ?? '').trim();
          const key = floor || '__unknown__';
          if (!grouped.has(key)) grouped.set(key, { floor, places: [] });
          grouped.get(key).places.push(child);
        });
        const floors = [...grouped.values()].sort((a, b) => {
          if (!a.floor) return 1;
          if (!b.floor) return -1;
          return a.floor.localeCompare(b.floor, undefined, { numeric: true });
        });
        if (floors.length) {
          showFloorPlaces({ container, floor: floors[0].floor, places: floors[0].places, floors, preserveViewport: true, entrySource: 'gps' });
        }
      })
      .catch(() => { })
      .finally(() => {
        autoComplexEntryRef.current = false;
      });
  }, [complexMode, polygonPlaces, selectedPlace, showFloorPlaces, userPosition]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return undefined;

    const maybeEnterFromZoom = async () => {
      if (complexMode || selectedPlace || autoComplexEntryRef.current) return;
      const mapCenter = map.getCenter();
      const point = [mapCenter.lng, mapCenter.lat];
      const container = polygonPlaces.find((place) => (
        place.placeKind === 'container' && pointInFootprint(point, place.footprint)
      ));
      const canvas = map.getCanvas();
      const coverage = container
        ? footprintScreenCoverage(container.footprint, mapCenter.lat, map.getZoom(), canvas.clientWidth, canvas.clientHeight)
        : 0;
      if (!container || coverage < AUTO_COMPLEX_ENTER_COVERAGE) {
        blockedZoomComplexIdRef.current = null;
        return;
      }
      if (String(blockedZoomComplexIdRef.current || '') === String(container.id)) return;

      autoComplexEntryRef.current = true;
      try {
        const children = await getContainedPlaces(container.id);
        const grouped = new Map();
        (Array.isArray(children) ? children : []).forEach((child) => {
          const floor = String(child?.containment?.floor ?? '').trim();
          const key = floor || '__unknown__';
          if (!grouped.has(key)) grouped.set(key, { floor, places: [] });
          grouped.get(key).places.push(child);
        });
        const floors = [...grouped.values()].sort((a, b) => {
          if (!a.floor) return 1;
          if (!b.floor) return -1;
          return a.floor.localeCompare(b.floor, undefined, { numeric: true });
        });
        if (floors.length) {
          showFloorPlaces({ container, floor: floors[0].floor, places: floors[0].places, floors, preserveViewport: true, entrySource: 'zoom' });
        }
      } catch {
        // Keep the global map available if the complex contents cannot be loaded.
      } finally {
        autoComplexEntryRef.current = false;
      }
    };

    maybeEnterFromZoom();
    map.on('moveend', maybeEnterFromZoom);
    return () => map.off('moveend', maybeEnterFromZoom);
  }, [complexMode, mapReady, polygonPlaces, selectedPlace, showFloorPlaces]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !complexMode || complexMode.entrySource === 'gps') return undefined;
    const closeWhenZoomedOut = () => {
      const mapCenter = map.getCenter();
      const canvas = map.getCanvas();
      const coverage = footprintScreenCoverage(
        complexMode.container?.footprint,
        mapCenter.lat,
        map.getZoom(),
        canvas.clientWidth,
        canvas.clientHeight,
      );
      const containerLongitude = Number(complexMode.container?.longitude);
      const containerLatitude = Number(complexMode.container?.latitude);
      const distanceMeters = Number.isFinite(containerLongitude) && Number.isFinite(containerLatitude)
        ? new maplibregl.LngLat(containerLongitude, containerLatitude).distanceTo(mapCenter)
        : 0;
      if (distanceMeters <= AUTO_COMPLEX_EXIT_DISTANCE_METERS &&
        coverage >= AUTO_COMPLEX_EXIT_COVERAGE) return;
      setComplexQuery('');
      setFloorPlaces([]);
      setComplexMode(null);
      viewportBeforeComplexRef.current = null;
    };
    map.on('moveend', closeWhenZoomedOut);
    return () => map.off('moveend', closeWhenZoomedOut);
  }, [complexMode]);
  const changeLanguage = async (event) => {
    await setAppLanguage(event.target.value);
    setSearchResults([]);
    setSelectedPlace(null);
    setError('');
  };

  return (
    <Stack component="main" sx={sx.app}>
      <Box ref={mapContainerRef} sx={sx.map} />

      <HeaderControls
        language={language}
        mode={mode}
        onLanguageChange={changeLanguage}
        onToggleMode={toggleMode}
        onAddPlace={() => setNewPlaceDraft({
          id: 'new-place',
          name: '', address: '', city: '', country: '',
          latitude: center.latitude, longitude: center.longitude,
          openingHours: [], footprint: null,
        })}
        onAbout={() => setAboutOpen(true)}
      />
      <SearchOverlay
        query={query}
        onQueryChange={setQuery}
        onSubmit={submitSearch}
        onClear={clearSearch}
        searching={searching}
        results={searchResults}
        selectedPlace={selectedPlace}
        onSelect={selectResult}
        hidden={Boolean(complexMode)}
      />
      <MapActions
        onLocate={() => mapRef.current?.flyTo({
          center: [center.longitude, center.latitude],
          zoom: 17.5,
          duration: 700,
        })}
        locationStatus={locationStatus}
        error={error}
      />
      <Stack
        spacing={0.8}
        aria-hidden={!complexMode}
        sx={{
          position: 'absolute',
          zIndex: 7,
          top: { xs: 'calc(max(10px, env(safe-area-inset-top)) + 54px)', sm: 22 },
          left: '50%',
          maxWidth: 'calc(100vw - 20px)',
          width: { xs: 'calc(100vw - 20px)', sm: 'min(620px, calc(100vw - 260px))' },
          px: 1.2,
          py: 1,
          borderRadius: { xs: '16px', sm: '20px' },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 18px 60px rgba(17,39,35,.18)',
          backdropFilter: 'blur(18px)',
          opacity: complexMode ? 1 : 0,
          transform: complexMode
            ? 'translate(-50%, 0) scale(1)'
            : 'translate(-50%, 10px) scale(.985)',
          pointerEvents: complexMode ? 'auto' : 'none',
          transition: 'opacity 220ms ease, transform 300ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        {!!complexMode && (
          <Stack
            spacing={0.8}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "space-between"
              }}

              spacing={1}
            >

              <Typography noWrap sx={{ fontWeight: 600 }} >
                {placeLabel(complexMode.container)}
              </Typography>

              <Stack sx={{ position: 'relative', flex: 1, minWidth: 100 }}>
                <Stack direction="row" sx={{
                  alignItems: 'center', px: 1.2, borderRadius: '12px',
                  bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider',
                }}>
                  <SearchRounded sx={{ color: 'text.secondary', fontSize: 21 }} />
                  <InputBase
                    value={complexQuery}
                    onChange={(event) => setComplexQuery(event.target.value)}
                    placeholder={t('containedPlaces.searchPlaceholder')}
                    inputProps={{ 'aria-label': t('containedPlaces.searchPlaceholder') }}
                    sx={{ flex: 1, minWidth: 0, px: 0.8, fontSize: 14 }}
                  />
                  {!!complexQuery && <IconButton size="small" onClick={() => setComplexQuery('')}><Close fontSize="small" /></IconButton>}
                </Stack>
                {!!complexQuery.trim() && (
                  <Stack sx={{
                    position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
                    zIndex: 9, maxHeight: 330, overflowY: 'auto', p: 0.7,
                    borderRadius: '14px', bgcolor: 'background.paper',
                    border: '1px solid', borderColor: 'divider',
                    boxShadow: '0 14px 45px rgba(17,39,35,.2)',
                  }}>
                    {complexSearchResults.length ? complexSearchResults.map(({ place, floor }) => (
                      <Button key={place.id} color="inherit" onClick={() => {
                        if (floor.floor !== complexMode.floor) {
                          showFloorPlaces({ container: complexMode.container, floor: floor.floor, places: floor.places, floors: complexMode.floors });
                        }
                        setComplexQuery('');
                        openPlace(place);
                      }} sx={{ justifyContent: 'flex-start', textAlign: 'start', borderRadius: '10px', py: 1 }}>
                        <Stack sx={{ minWidth: 0, alignItems: 'flex-start' }}>
                          <Typography noWrap sx={{ maxWidth: '100%', fontWeight: 750, fontSize: 14 }}>{placeLabel(place)}</Typography>
                          <Typography noWrap color="text.secondary" sx={{ maxWidth: '100%', fontSize: 12 }}>
                            {[place.categories?.[0]?.displayName || place.categories?.[0], floor.floor && `${t('containedPlaces.floorLabel')} ${floor.floor}`].filter(Boolean).join(' · ')}
                          </Typography>
                        </Stack>
                      </Button>
                    )) : <Typography color="text.secondary" sx={{ p: 1.2, fontSize: 13 }}>{t('containedPlaces.noSearchResults')}</Typography>}
                  </Stack>
                )}
              </Stack>



              <IconButton onClick={exitComplexMode}><Close /></IconButton>

            </Stack>
            {
              complexMode.floors.length > 1
                ? <Stack direction="row" spacing={0.7} sx={{ overflowX: 'auto', pb: 0.2 }}>
                  {complexMode.floors?.map((floor) => (
                    <Button
                      key={floor.floor || '__unknown__'}
                      size="small"
                      variant={floor.floor === complexMode.floor ? 'contained' : 'outlined'}
                      onClick={() => showFloorPlaces({
                        container: complexMode.container,
                        floor: floor.floor,
                        places: floor.places,
                        floors: complexMode.floors,
                      })}
                      sx={{ flex: '0 0 auto', borderRadius: '10px' }}
                    >
                      {floor.floor ? floor.floor : t('containedPlaces.unknownFloor')}
                    </Button>
                  ))}
                </Stack>
                : null
            }
          </Stack>
        )}
      </Stack>
      <PlaceDetailsSheet
        place={selectedPlace}
        loading={placeLoading}
        onClose={closePlace}
        onPlaceSelect={openPlace}
        onFloorSelect={showFloorPlaces}
      />
      <PlaceEditDialog
        open={Boolean(newPlaceDraft)}
        place={newPlaceDraft}
        mode={newPlaceDraft?.dialogMode || "create"}
        onClose={() => setNewPlaceDraft(null)}
      />
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <Dialog open={continuationDesktopOnlyOpen} onClose={() => setContinuationDesktopOnlyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('addPlace.desktopLinkOnlyTitle')}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info">{t('addPlace.desktopLinkOnlyText')}</Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setContinuationDesktopOnlyOpen(false)}>{t('placeEdit.close')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
